import React, { useState } from 'react';
import CustomIcon from './CustomIcon';

interface GoogleDrivePlayerProps {
  videoUrl: string;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

const GoogleDrivePlayer: React.FC<GoogleDrivePlayerProps> = ({
  videoUrl,
  onProgress,
  onComplete,
  onTimeUpdate
}) => {
  // All hooks must be called at the top level before any conditional returns
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);

  // Early return if videoUrl is undefined, null, or empty
  if (!videoUrl || typeof videoUrl !== 'string') {
    console.warn('GoogleDrivePlayer: Invalid videoUrl provided:', videoUrl, typeof videoUrl);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700">No video URL provided</p>
        <p className="text-xs text-red-600 mt-1">Type: {typeof videoUrl}, Value: {String(videoUrl)}</p>
      </div>
    );
  }

  // Extract Google Drive file ID
  const getGoogleDriveId = (url: string): string | null => {
    if (!url || typeof url !== 'string') {
      return null;
    }
    
    const patterns = [
      /drive\.google\.com\/file\/d\/([^\/]+)/,
      /drive\.google\.com\/open\?id=([^&\n?#]+)/,
      /drive\.google\.com\/uc\?id=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const fileId = getGoogleDriveId(videoUrl);
  
  // Log if no valid file ID was extracted
  if (!fileId) {
    console.warn('GoogleDrivePlayer: Could not extract file ID from URL:', videoUrl);
  }
  
  const previewUrl = fileId ? `https://drive.google.com/file/d/${fileId}/preview` : '';
  const downloadUrl = fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : '';

  if (!fileId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700">Invalid Google Drive URL</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Google Drive Preview */}
      <div className="relative bg-black rounded-lg overflow-hidden">
        <iframe
          src={previewUrl}
          className="w-full h-48 sm:h-64 md:h-80 lg:h-96"
          frameBorder="0"
          allow="autoplay"
          title="Google Drive video preview"
        />
      </div>
      
      {/* Manual Progress Tracking */}
      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Progress Tracking</h3>
        <p className="text-gray-600 text-xs sm:text-sm mb-3">
          Since this is an external video, please manually track your progress.
        </p>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              const newProgress = Math.min(progress + 25, 100);
              setProgress(newProgress);
              onProgress?.(newProgress);
              if (newProgress >= 100) {
                onComplete?.();
              }
            }}
            className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Mark 25% Complete
          </button>
          <button
            onClick={() => {
              const newProgress = Math.min(progress + 50, 100);
              setProgress(newProgress);
              onProgress?.(newProgress);
              if (newProgress >= 100) {
                onComplete?.();
              }
            }}
            className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Mark 50% Complete
          </button>
          <button
            onClick={() => {
              setProgress(100);
              onProgress?.(100);
              onComplete?.();
            }}
            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Mark Complete
          </button>
        </div>
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-primary-600 h-1.5 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            {progress}% completed
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoogleDrivePlayer;
